import React, { useEffect, useState, useRef } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import "./Nav.css";
import logo from "../../assets/logo.png";
import { useSelector } from "react-redux";
import { selectUser } from "../../features/userSlice";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch, faTimes, faFilm, faTv, faBars } from "@fortawesome/free-solid-svg-icons";
import axios from "../../api/axios";
import apiUrl from "../../api/config.json";

const API_KEY = apiUrl.APIKey;
const base_url = "https://image.tmdb.org/t/p/original";

const navItems = [
	{ label: "Home", path: "/" },
	{ label: "Movies", path: "/movies" },
	{ label: "TV Shows", path: "/tv-shows" },
	{ label: "Anime", path: "/anime" },
	{ label: "New & Popular", path: "/new-popular" },
];

function Nav() {
	const [show, setShow] = useState(false);
	const [searchOpen, setSearchOpen] = useState(false);
	const [menuOpen, setMenuOpen] = useState(false);
	const [query, setQuery] = useState("");
	const [results, setResults] = useState([]);
	const [searching, setSearching] = useState(false);
	const history = useNavigate();
	const location = useLocation();
	const user = useSelector(selectUser);
	const searchRef = useRef(null);
	const inputRef = useRef(null);
	const menuRef = useRef(null);

	useEffect(() => {
		const onScroll = () => setShow(window.scrollY > 50);
		window.addEventListener("scroll", onScroll);
		return () => window.removeEventListener("scroll", onScroll);
	}, []);

	// Close menus on route change
	useEffect(() => {
		setSearchOpen(false);
		setMenuOpen(false);
		setQuery("");
		setResults([]);
	}, [location.pathname]);

	// Focus input when search opens
	useEffect(() => {
		if (searchOpen && inputRef.current) inputRef.current.focus();
	}, [searchOpen]);

	// Close on outside click
	useEffect(() => {
		const handleClick = (e) => {
			if (searchRef.current && !searchRef.current.contains(e.target)) {
				setSearchOpen(false);
			}
			if (menuRef.current && !menuRef.current.contains(e.target)) {
				setMenuOpen(false);
			}
		};
		document.addEventListener("mousedown", handleClick);
		return () => document.removeEventListener("mousedown", handleClick);
	}, []);

	// Debounced search
	useEffect(() => {
		if (!query.trim()) {
			setResults([]);
			return;
		}
		const timer = setTimeout(async () => {
			setSearching(true);
			try {
				const res = await axios.get(
					`/search/multi?api_key=${API_KEY}&language=en-US&query=${encodeURIComponent(query)}&page=1`
				);
				const filtered = res.data.results.filter(
					(item) => item.media_type !== "person" && (item.poster_path || item.backdrop_path)
				);
				setResults(filtered.slice(0, 10));
			} catch (err) {
				console.log("Search error:", err);
			} finally {
				setSearching(false);
			}
		}, 400);
		return () => clearTimeout(timer);
	}, [query]);

	return (
		<div className={`nav ${show ? "nav__scrolled" : ""}`}>
			<div className="nav__contents">
				<div className="nav__left">
					<img
						onClick={() => history("/")}
						className="nav__logo"
						src={logo}
						alt="RokFlix"
					/>
					<ul className="nav__links">
						{navItems.map((item) => (
							<li key={item.path}>
								<Link
									to={item.path}
									className={`nav__link ${location.pathname === item.path ? "active" : ""}`}
								>
									{item.label}
								</Link>
							</li>
						))}
					</ul>
				</div>

				<div className="nav__right" ref={searchRef}>
					<div className={`nav__search ${searchOpen ? "nav__search--open" : ""}`}>
						<button
							className="nav__search-btn"
							aria-label="Search"
							onClick={() => {
								if (searchOpen) {
									setSearchOpen(false);
									setQuery("");
									setResults([]);
								} else {
									setSearchOpen(true);
								}
							}}
						>
							<FontAwesomeIcon icon={searchOpen ? faTimes : faSearch} />
						</button>
						{searchOpen && (
							<input
								ref={inputRef}
								className="nav__search-input"
								type="text"
								placeholder="Search movies, TV shows..."
								value={query}
								onChange={(e) => setQuery(e.target.value)}
							/>
						)}
						{searchOpen && query && (
							<button
								className="nav__search-close"
								onClick={() => { setQuery(""); setResults([]); }}
								aria-label="Clear"
							>
								<FontAwesomeIcon icon={faTimes} />
							</button>
						)}
					</div>

					{searchOpen && (query || results.length > 0) && (
						<div className="nav__search-dropdown">
							{searching && (
								<div className="nav__search-loading">Searching...</div>
							)}
							{!searching && results.length > 0 && results.map((item) => (
								<Link
									key={item.id}
									to={`/movie/${item.id}`}
									className="nav__search-result"
								>
									<img
										src={`${base_url}${item.poster_path || item.backdrop_path}`}
										alt={item.title || item.name}
										className="nav__search-result-img"
									/>
									<div className="nav__search-result-info">
										<span className="nav__search-result-title">
											{item.title || item.name}
										</span>
										<span className="nav__search-result-meta">
											<FontAwesomeIcon
												icon={item.media_type === "tv" ? faTv : faFilm}
												style={{ marginRight: 4 }}
											/>
											{item.media_type === "tv" ? "TV Show" : "Movie"}
											{item.vote_average > 0 && (
												<> &middot; {item.vote_average.toFixed(1)} &#9733;</>
											)}
											{(item.release_date || item.first_air_date) && (
												<> &middot; {(item.release_date || item.first_air_date).split("-")[0]}</>
											)}
										</span>
									</div>
								</Link>
							))}
							{!searching && query && results.length === 0 && (
								<div className="nav__search-empty">
									No results for &quot;{query}&quot;
								</div>
							)}
						</div>
					)}

					{!user ? (
						<button
							className="login_button"
							onClick={() => history("/landing")}
						>
							Sign In
						</button>
					) : (
						<button
							className="nav__avatar-btn"
							onClick={() => history("/profile")}
							aria-label="Profile"
						>
							<img
								className="nav__avatar"
								src="https://upload.wikimedia.org/wikipedia/commons/0/0b/Netflix-avatar.png?20201013161117"
								alt="Profile"
							/>
						</button>
					)}

					{/* Mobile hamburger */}
					<button
						className="nav__hamburger"
						onClick={() => setMenuOpen(!menuOpen)}
						aria-label="Menu"
					>
						<FontAwesomeIcon icon={faBars} />
					</button>
				</div>
			</div>

			{/* Mobile menu overlay */}
			{menuOpen && (
				<div className="nav__mobile-overlay" onClick={() => setMenuOpen(false)} />
			)}
			{/* Mobile menu drawer */}
			<div ref={menuRef} className={`nav__mobile-menu ${menuOpen ? "nav__mobile-menu--open" : ""}`}>
				<div className="nav__mobile-header">
					<img onClick={() => { history("/"); setMenuOpen(false); }} className="nav__mobile-logo" src={logo} alt="RokFlix" />
					<button className="nav__mobile-close" onClick={() => setMenuOpen(false)} aria-label="Close menu">
						<FontAwesomeIcon icon={faTimes} />
					</button>
				</div>
				<ul className="nav__mobile-links">
					{navItems.map((item) => (
						<li key={item.path}>
							<Link
								to={item.path}
								className={`nav__mobile-link ${location.pathname === item.path ? "active" : ""}`}
								onClick={() => setMenuOpen(false)}
							>
								{item.label}
							</Link>
						</li>
					))}
				</ul>
				<div className="nav__mobile-footer">
					{!user ? (
						<button className="login_button" style={{ width: "100%" }} onClick={() => { history("/landing"); setMenuOpen(false); }}>
							Sign In
						</button>
					) : (
						<button className="nav__mobile-profile" onClick={() => { history("/profile"); setMenuOpen(false); }}>
							<img
								src="https://upload.wikimedia.org/wikipedia/commons/0/0b/Netflix-avatar.png?20201013161117"
								alt="Profile"
							/>
							<span>Profile</span>
						</button>
					)}
				</div>
			</div>
		</div>
	);
}

export default Nav;
